// SubmitYourMusicPage.js
// (last update: 29 Jul 2025 — plan buttons: Corner Cut + glow + flash)

import React, { useEffect, useState } from 'react';
import './SubmitYourMusicPage.css';
import { applyRandomFlicker, decodeTextLocked, runAfterFontsReady } from './utils/textEffects';
import { getAnalyticsSessionId, trackSiteEvent } from './siteAnalytics';
import { useRegionalVisibility } from './regionalVisibility';
import PaymentMaintenanceModal from './components/PaymentMaintenanceModal';
import { PAYMENTS_TEMPORARILY_UNAVAILABLE } from './paymentMaintenance';

const FEEDBACK_MEDIA = [
  { type: 'video', src: require('./feedback/00_feedback.mp4') },
  { type: 'image', src: require('./feedback/01_feedback.jpg') },
  { type: 'video', src: require('./feedback/02_feedback.mp4') },
  { type: 'video', src: require('./feedback/03_feedback.mp4') },
  { type: 'video', src: require('./feedback/04_feedback.mp4') },
  { type: 'video', src: require('./feedback/05_feedback.mp4') },
  { type: 'video', src: require('./feedback/06_feedback.mp4') },
  { type: 'video', src: require('./feedback/07_feedback.mp4') },
  { type: 'video', src: require('./feedback/08_feedback.mp4') },
  { type: 'video', src: require('./feedback/09_feedback.mp4') },
  { type: 'video', src: require('./feedback/010_feedback.mp4') },
  { type: 'video', src: require('./feedback/011_feedback.mp4') },
  { type: 'video', src: require('./feedback/012_feedback.mp4') },
  { type: 'video', src: require('./feedback/013_feedback.mp4') },
  { type: 'video', src: require('./feedback/014_feedback.mp4') },
  { type: 'video', src: require('./feedback/015_feedback.mp4') },
  { type: 'video', src: require('./feedback/016_feedback.mp4') },
  { type: 'video', src: require('./feedback/017_feedback.mp4') },
];

const SUBMIT_BACKGROUND_GIF = `${process.env.PUBLIC_URL}/static/img/playlist-anim.gif`;
const SUBMITHUB_PROFILE_URL = 'https://www.submithub.com/playlister/iva-gonzo';
const INSTAGRAM_PROFILE_URL = 'https://www.instagram.com/ivagonzo/';
const RAW_API_BASE =
  process.env.REACT_APP_BACKEND_BASE_URL ||
  process.env.REACT_APP_API_BASE_URL ||
  '';
const API_BASE_URL = normalizeApiBase(RAW_API_BASE);
const PAY_START_ENDPOINT = buildApiUrl('/pay/start');

function normalizeApiBase(base) {
  if (!base) return '';
  const trimmed = String(base).trim();
  if (trimmed === '/' || trimmed === '/api') return '/api';
  return trimmed.replace(/\/$/, '');
}

function buildApiUrl(path) {
  const cleanPath = path.startsWith('/') ? path : `/${path}`;

  if (!API_BASE_URL) {
    return `/api${cleanPath}`.replace(/\/api\/+/, '/api/');
  }

  if (API_BASE_URL === '/api') {
    return `${API_BASE_URL}${cleanPath}`.replace(/\/api\/+/, '/api/');
  }

  const normalizedBase = API_BASE_URL.replace(/\/$/, '');
  if (/\/api$/i.test(normalizedBase)) {
    return `${normalizedBase}${cleanPath}`.replace(/\/api\/+/, '/api/');
  }

  return `${normalizedBase}/api${cleanPath}`.replace(/\/api\/+/, '/api/');
}

function PlanWord({ plan, children, intro = false, revealed = true }) {
  const classes = ['plan-word', `plan-word--${plan}`];
  if (intro) classes.push('plan-word--intro');
  if (intro && revealed) classes.push('plan-word--revealed');
  return <span className={classes.join(' ')}>{children}</span>;
}

function scrollExpandedSectionIntoView(sectionId, triggerSelector) {
  if (typeof window === 'undefined') return;

  const section = document.getElementById(sectionId);
  if (!section) return;

  const trigger = triggerSelector ? document.querySelector(triggerSelector) : null;
  const sectionRect = section.getBoundingClientRect();
  const triggerRect = trigger ? trigger.getBoundingClientRect() : sectionRect;
  const nextTrigger = section.nextElementSibling?.matches?.('.btn-link[data-text]')
    ? section.nextElementSibling
    : null;
  const nextTriggerRect = nextTrigger ? nextTrigger.getBoundingClientRect() : null;
  const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
  const scrollY = window.scrollY || window.pageYOffset || 0;
  const margin = Math.min(28, Math.max(14, viewportHeight * 0.04));
  const nextSectionPeek = nextTriggerRect ? Math.min(14, Math.max(8, viewportHeight * 0.018)) : 0;
  const availableHeight = Math.max(160, viewportHeight - margin * 2);
  const targetTop = Math.min(triggerRect.top, sectionRect.top) + scrollY;
  const targetBottom = (nextTriggerRect ? nextTriggerRect.bottom : sectionRect.bottom) + scrollY + nextSectionPeek;
  const targetHeight = targetBottom - targetTop;
  const viewportTop = scrollY + margin;
  const viewportBottom = scrollY + viewportHeight - margin;

  let nextTop = null;

  if (targetHeight > availableHeight) {
    nextTop = nextTriggerRect ? targetBottom - viewportHeight + margin : targetTop - margin;
  } else if (targetTop < viewportTop) {
    nextTop = targetTop - margin;
  } else if (targetBottom > viewportBottom) {
    nextTop = targetBottom - viewportHeight + margin;
  }

  if (nextTop === null) return;

  window.scrollTo({
    top: Math.max(0, nextTop),
    behavior: 'smooth',
  });
}

function getPayStartErrorMessage(rawCode) {
  const code = String(rawCode || '').trim().toUpperCase();
  if (code === 'PAYMENTS_TEMPORARILY_UNAVAILABLE') {
    return 'Payments are temporarily unavailable. Please try again later.';
  }
  if (code === 'INVALID_EMAIL') return 'Enter a valid email.';
  if (code === 'UNKNOWN_PLAN') return 'Selected plan is unavailable right now. Please refresh the page.';
  if (code === 'WHOP_API_KEY_MISSING') return 'Payments are temporarily unavailable. Please contact support.';
  if (code === 'D1_NOT_CONFIGURED') return 'Server payment storage is not configured yet.';
  if (code === 'ORDER_INIT_FAILED') return 'Could not create order. Please try again.';
  if (code === 'WHOP_CHECKOUT_CREATE_FAILED') return 'Could not open checkout. Please try again in a minute.';
  if (code === 'TOO_MANY_REQUESTS') return 'Too many checkout attempts. Please wait a few minutes and try again.';
  if (code === 'RATE_LIMIT_UNAVAILABLE') return 'Payments are temporarily unavailable. Please try again shortly.';
  return 'Could not start payment. Please try again.';
}

/* ---------- main component ---------- */
function SubmitYourMusicPage() {
  const { hideInstagram } = useRegionalVisibility();
  /* UI state */
  const [showHowItWorks, setShowHowItWorks] = useState(false);
  const [showGenres, setShowGenres] = useState(false);
  const [showImportantToKnow, setShowImportantToKnow] = useState(false);
  const [showFaq, setShowFaq] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [currentFeedbackIndex, setCurrentFeedbackIndex] = useState(0);
  const [showForm, setShowForm] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState('quick_check');
  const [introComplete, setIntroComplete] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [purchaseStep, setPurchaseStep] = useState('plans');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [highlightGlitch, setHighlightGlitch] = useState(false);
  const [isFeedbackSwitching, setIsFeedbackSwitching] = useState(false);
  const [revealedHeroPlans, setRevealedHeroPlans] = useState([]);
  const [stampDimmed, setStampDimmed] = useState(false);
  const [stampBright, setStampBright] = useState(false);
  const [stampBrightFading, setStampBrightFading] = useState(false);
  const [showCuratorTrust, setShowCuratorTrust] = useState(false);
  const [showPaymentMaintenance, setShowPaymentMaintenance] = useState(false);

  /* flash token for click animation */
  const [flashPlan, setFlashPlan] = useState('');
  const [flashTick, setFlashTick] = useState(0);

  /* legal modals */
  const [showPrivacyPolicy, setShowPrivacyPolicy] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [showRefundPolicy, setShowRefundPolicy] = useState(false);

  const waveTimeoutsRef = React.useRef([]); // таймеры интро-волны
  const feedbackVideoRef = React.useRef(null);
  const feedbackAutoAdvanceRef = React.useRef(null);
  const highlightGlitchTimeoutRef = React.useRef(null);
  const highlightGlitchDurationRef = React.useRef(null);
  const feedbackSwitchTimeoutRef = React.useRef(null);
  const feedbackVolumeIntervalRef = React.useRef(null);
  const heroPlanRevealTimeoutsRef = React.useRef([]);
  const stampDimTimeoutRef = React.useRef(null);
  const stampBrightTimeoutRef = React.useRef(null);
  const stampBrightFadeTimeoutRef = React.useRef(null);

  const scheduleHighlightGlitch = React.useCallback(() => {
    const min = 3500;
    const max = 9000;
    const timeout = Math.floor(Math.random() * (max - min)) + min;
    highlightGlitchTimeoutRef.current = setTimeout(() => {
      setHighlightGlitch(true);
      highlightGlitchDurationRef.current = setTimeout(() => {
        setHighlightGlitch(false);
        scheduleHighlightGlitch();
      }, 500);
    }, timeout);
  }, []);

  const feedbackMedia = FEEDBACK_MEDIA;

  const flashCuratorStamp = React.useCallback(() => {
    if (stampBrightTimeoutRef.current) clearTimeout(stampBrightTimeoutRef.current);
    if (stampBrightFadeTimeoutRef.current) clearTimeout(stampBrightFadeTimeoutRef.current);
    setStampBrightFading(false);
    setStampBright(true);
    stampBrightFadeTimeoutRef.current = setTimeout(() => {
      setStampBrightFading(true);
      stampBrightFadeTimeoutRef.current = null;
    }, 2000);
    stampBrightTimeoutRef.current = setTimeout(() => {
      setStampBright(false);
      setStampBrightFading(false);
      stampBrightTimeoutRef.current = null;
    }, 3500);
  }, []);

  /* ------------ effects ------------ */
  useEffect(() => {
    const cleanups = [];
    let isMounted = true;

    const startEffects = () => {
      if (!isMounted) {
        return;
      }

      const links = document.querySelectorAll('.btn-link, .section-title');
      links.forEach((link) => {
        cleanups.push(decodeTextLocked(link, link.dataset.text, 500));
        cleanups.push(applyRandomFlicker(link));
      });
    };

    const cancelFontWait = runAfterFontsReady(startEffects);

    resetGlowEffect();
    if (window.innerWidth >= 768) document.body.classList.add('desktop');

    /* social icons slide-in */
    document.querySelectorAll('#social-icons a').forEach((icon, i) => {
      setTimeout(() => {
        icon.style.opacity = '1';
        icon.style.transform = 'translateY(0)';
      }, i * 600);
    });

    /* section fade-ins */
    setTimeout(() => {
      document.querySelectorAll('.section-title, .permanent-show-section, .hero-subtitle').forEach((el) => {
        el.classList.add('fade-in');
      });
    }, 0);

    return () => {
      isMounted = false;
      cancelFontWait();
      cleanups.forEach((cleanup) => {
        if (typeof cleanup === 'function') {
          cleanup();
        }
      });
    };
  }, []);

  useEffect(() => {
    stampDimTimeoutRef.current = setTimeout(() => {
      setStampDimmed(true);
    }, 8400);

    return () => {
      if (stampDimTimeoutRef.current) clearTimeout(stampDimTimeoutRef.current);
      if (stampBrightTimeoutRef.current) clearTimeout(stampBrightTimeoutRef.current);
      if (stampBrightFadeTimeoutRef.current) clearTimeout(stampBrightFadeTimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const params = new URLSearchParams(window.location.search);
    const modal = String(params.get('modal') || '').trim().toLowerCase();

    if (modal === 'terms') {
      setShowTerms(true);
    } else if (modal === 'privacy') {
      setShowPrivacyPolicy(true);
    } else if (modal === 'refund') {
      setShowRefundPolicy(true);
    }
  }, []);

  useEffect(() => {
    scheduleHighlightGlitch();
    return () => {
      if (highlightGlitchTimeoutRef.current) clearTimeout(highlightGlitchTimeoutRef.current);
      if (highlightGlitchDurationRef.current) clearTimeout(highlightGlitchDurationRef.current);
    };
  }, [scheduleHighlightGlitch]);

  useEffect(() => {
    const clearHeroPlanRevealTimeouts = () => {
      heroPlanRevealTimeoutsRef.current.forEach((id) => clearTimeout(id));
      heroPlanRevealTimeoutsRef.current = [];
    };

    const plans = ['quick', 'standard', 'premium'];
    const order = plans
      .map((plan) => ({ plan, sort: Math.random() }))
      .sort((a, b) => a.sort - b.sort)
      .map((item) => item.plan);

    setRevealedHeroPlans([]);
    clearHeroPlanRevealTimeouts();

    let delay = 480;
    order.forEach((plan, index) => {
      const timeoutId = window.setTimeout(() => {
        setRevealedHeroPlans((current) => (current.includes(plan) ? current : [...current, plan]));
      }, delay);
      heroPlanRevealTimeoutsRef.current.push(timeoutId);
      delay += 980 + (index * 180) + Math.round(Math.random() * 260);
    });

    return () => {
      clearHeroPlanRevealTimeouts();
    };
  }, []);

    /* INTRO WAVE — 3 цикла, случайный порядок, замедление к концу, авто-выбор premium_1 */
    useEffect(() => {
      const plans = ['quick_check','standard_1','standard_5','standard_10','premium_1','premium_5','premium_10'];

      // случайная перестановка для каждого круга
      const shuffle = (arr) => {
        const a = arr.slice();
        for (let i = a.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [a[i], a[j]] = [a[j], a[i]];
        }
        return a;
      };

      // три полных круга (каждый — в своём рандомном порядке)
      const sequence = [...shuffle(plans), ...shuffle(plans), ...shuffle(plans), ...shuffle(plans)];

      // скорость: старт как сейчас; EXTRA_MS — сколько добавим к задержке к финалу (замедление)
      const BASE_MS  = 60;   // ваша текущая скорость шага
      const EXTRA_MS = 150;   // чем больше, тем сильнее замедление в конце
      const DECEL_START = 0.70; // замедляемся только на последних 15% пути (0.80–0.90 по вкусу)
      const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

      const clearAll = () => {
        document.querySelectorAll('.submission-button.demo-selected')
          .forEach((b) => b.classList.remove('demo-selected'));
      };

      function finalizeToQuickCheck() {
        clearAll();
        setSelectedPlan('quick_check'); // реальный выбор
        setShowForm(true);            // геометрия уже зафиксирована, здесь только подтверждаем стартовый план
        setIntroComplete(true);
      }

      let i = 0;
      const tick = () => {
        clearAll();
        const plan = sequence[i];
        const btn = document.querySelector(`button[data-plan="${plan}"]`);
        if (btn) btn.classList.add('demo-selected'); // визуальная подсветка шага

        i += 1;
        if (i < sequence.length) {
          // прогресс от 0 до 1; ближе к 1 — задержка больше (замедляемся)
          const p = i / sequence.length;
          // до порога — задержка почти BASE_MS; после — плавно наращиваем
          const localP = p <= DECEL_START ? 0 : (p - DECEL_START) / (1 - DECEL_START);
          const delay = Math.round(BASE_MS + EXTRA_MS * easeOutCubic(localP));
          const id = setTimeout(tick, delay);
          waveTimeoutsRef.current.push(id);
        } else {
          finalizeToQuickCheck(); // стоп на quick_check (реальный выбор)
        }
      };

      // старт волны (можно сделать 150 для более раннего старта)
      waveTimeoutsRef.current.push(setTimeout(tick, 150));

      // отменить волну при первом клике пользователя
      const cancelOnUserAction = () => {
        waveTimeoutsRef.current.forEach(clearTimeout);
        waveTimeoutsRef.current = [];
        clearAll();
      };
      const buttons = document.querySelectorAll('.submission-button');
      buttons.forEach((b) => b.addEventListener('click', cancelOnUserAction, { once: true }));

      // очистка на размонтировании
      return () => {
        waveTimeoutsRef.current.forEach(clearTimeout);
        waveTimeoutsRef.current = [];
        buttons.forEach((b) => b.removeEventListener('click', cancelOnUserAction));
      };
    }, []);

  /* ------------ helper togglers ------------ */
  const resetGlowEffect = () =>
    document.querySelectorAll('.btn-link').forEach((l) => l.classList.remove('open-section'));

  const openFeedbackFromTrust = (event) => {
    event.preventDefault();
    setShowCuratorTrust(false);
    setShowHowItWorks(false);
    setShowGenres(false);
    setShowImportantToKnow(false);
    setShowFaq(false);
    setShowFeedback(true);
    resetGlowEffect();

    requestAnimationFrame(() => {
      const btn = document.querySelector('.btn-link[data-text="FEEDBACK"]');
      if (btn) btn.classList.add('open-section');
      requestAnimationFrame(() => {
        scrollExpandedSectionIntoView('feedback-gallery', '.btn-link[data-text="FEEDBACK"]');
      });
    });
  };

  const makeToggle =
    (isOpenNow, setter, selector, sectionId) =>
    () => {
      const mapping = {
        setShowHowItWorks,
        setShowGenres,
        setShowImportantToKnow,
        setShowFaq,
        setShowFeedback,
      };

      const willOpen = !isOpenNow;

      Object.values(mapping).forEach((fn) => (fn === setter ? setter(willOpen) : fn(false)));

      resetGlowEffect();
      if (willOpen) {
        const btn = document.querySelector(selector);
        if (btn) btn.classList.add('open-section');
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            scrollExpandedSectionIntoView(sectionId, selector);
          });
        });
      }
    };

  /* ------------ pricing helpers ------------ */
  const getPriceDetails = () => {
    switch (selectedPlan) {
      case 'standard_1':
        return { price: '$13', discount: '' };
      case 'standard_5':
        return { price: '$60', discount: 'Save $5' };
      case 'standard_10':
        return { price: '$110', discount: 'Save $20' };
      case 'premium_1':
        return { price: '$27', discount: '' };
      case 'premium_5':
        return { price: '$125', discount: 'Save $10' };
      case 'premium_10':
        return { price: '$230', discount: 'Save $40' };
      case 'quick_check':
        return { price: '$5', discount: '' };
      default:
        return { price: '', discount: '' };
    }
  };
  const { price, discount } = getPriceDetails();

  const getSelectedPlanLabel = () => {
    switch (selectedPlan) {
      case 'standard_1':
        return '1 Standard Review';
      case 'standard_5':
        return '5 Standard Reviews';
      case 'standard_10':
        return '10 Standard Reviews';
      case 'premium_1':
        return '1 Premium Review';
      case 'premium_5':
        return '5 Premium Reviews';
      case 'premium_10':
        return '10 Premium Reviews';
      case 'quick_check':
        return 'Quick Check';
      default:
        return '';
    }
  };
  const selectedPlanLabel = getSelectedPlanLabel();

  const getSelectedPlanTone = () => {
    if (selectedPlan === 'quick_check') return 'quick';
    if (selectedPlan?.startsWith('standard_')) return 'standard';
    if (selectedPlan?.startsWith('premium_')) return 'premium';
    return 'neutral';
  };
  const selectedPlanTone = getSelectedPlanTone();
  const isPurchaseFocusMode = purchaseStep === 'confirm' && !showTerms;

  const handleSubmissionClick = (plan) => {
    // отменяем интро-волну и убираем демонстрационную подсветку
    waveTimeoutsRef.current.forEach(clearTimeout);
    waveTimeoutsRef.current = [];
    document.querySelectorAll('.submission-button.demo-selected')
      .forEach((b) => b.classList.remove('demo-selected'));

    setIntroComplete(true);
    setSelectedPlan(plan);
    setShowForm(true);
    setAgreed(false);
    setPurchaseStep('plans');
    setSubmitError('');

    // «тройная вспышка» для кликнутой кнопки — ваше поведение как было
    setFlashPlan(plan);
    setFlashTick((t) => t + 1);
    trackSiteEvent('plan_selected', {
      source_page: 'submit_your_music',
      plan_key: plan,
    });
  };

  const startPayment = async () => {
    if (isSubmitting) return;
    setSubmitError('');

    if (!selectedPlan) {
      setSubmitError('Choose a plan to continue.');
      return;
    }
    if (!agreed) {
      return;
    }

    if (PAYMENTS_TEMPORARILY_UNAVAILABLE) {
      trackSiteEvent('payment_maintenance_notice_opened', {
        source_page: 'submit_your_music',
        plan_key: selectedPlan,
      });
      setShowPaymentMaintenance(true);
      return;
    }

    setIsSubmitting(true);
    trackSiteEvent('continue_to_payment_clicked', {
      source_page: 'submit_your_music',
      plan_key: selectedPlan,
    });

    try {
      const response = await fetch(PAY_START_ENDPOINT, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          plan: selectedPlan,
          source_url: typeof window !== 'undefined' ? window.location.href : '',
          expectations_ack: true,
          expectations_ack_version: 'v1',
          analytics_session_id: getAnalyticsSessionId(),
        }),
      });

      let data = null;
      try {
        data = await response.json();
      } catch {
        data = null;
      }

      if (!response.ok || !data?.ok) {
        const serverCode = data?.error || `HTTP_${response.status}`;
        throw new Error(getPayStartErrorMessage(serverCode));
      }

      const redirectUrl = String(data.redirect_url || data.purchase_url || '').trim();
      if (!redirectUrl) {
        throw new Error('Checkout link is missing. Please try again.');
      }

      trackSiteEvent('pay_start_success', {
        source_page: 'submit_your_music',
        plan_key: data.plan_key || selectedPlan,
        order_id: data.order_id || '',
        whop_session_id: data.session_id || '',
      });
      window.location.assign(redirectUrl);
    } catch (err) {
      console.error('pay/start failed', err);
      trackSiteEvent('pay_start_error', {
        source_page: 'submit_your_music',
        plan_key: selectedPlan,
        error_code: err?.message || 'PAY_START_FAILED',
      });
      setSubmitError(err?.message || 'Could not start payment. Please try again.');
      setIsSubmitting(false);
    }
  };

  const handleFormSubmit = (e) => {
    if (e?.preventDefault) e.preventDefault();

    if (isSubmitting) return;
    setSubmitError('');

    if (!selectedPlan) {
      setSubmitError('Choose a plan to continue.');
      return;
    }

    trackSiteEvent('purchase_continue_clicked', {
      source_page: 'submit_your_music',
      plan_key: selectedPlan,
    });
    setPurchaseStep('confirm');
    trackSiteEvent('expectations_step_viewed', {
      source_page: 'submit_your_music',
      plan_key: selectedPlan,
    });
  };

  /* ------------ feedback gallery ------------ */
  const clearFeedbackAdvanceTimeout = React.useCallback(() => {
    if (feedbackAutoAdvanceRef.current) {
      clearTimeout(feedbackAutoAdvanceRef.current);
      feedbackAutoAdvanceRef.current = null;
    }
  }, []);

  const clearFeedbackVolumeRamp = React.useCallback(() => {
    if (feedbackVolumeIntervalRef.current) {
      clearInterval(feedbackVolumeIntervalRef.current);
      feedbackVolumeIntervalRef.current = null;
    }
  }, []);

  const nextFeedback = React.useCallback(() => {
    clearFeedbackAdvanceTimeout();
    setCurrentFeedbackIndex((i) => (i + 1) % feedbackMedia.length);
  }, [clearFeedbackAdvanceTimeout, feedbackMedia.length]);

  const prevFeedback = React.useCallback(() => {
    clearFeedbackAdvanceTimeout();
    setCurrentFeedbackIndex((i) => (i - 1 + feedbackMedia.length) % feedbackMedia.length);
  }, [clearFeedbackAdvanceTimeout, feedbackMedia.length]);

  const currentFeedbackMedia = feedbackMedia[currentFeedbackIndex];

  useEffect(() => {
    if (!showFeedback) {
      clearFeedbackAdvanceTimeout();
      clearFeedbackVolumeRamp();
      if (feedbackVideoRef.current) {
        feedbackVideoRef.current.pause();
        feedbackVideoRef.current.currentTime = 0;
        feedbackVideoRef.current.volume = 0;
      }
      return;
    }

    clearFeedbackAdvanceTimeout();
    clearFeedbackVolumeRamp();

    if (currentFeedbackMedia.type === 'video') {
      const videoEl = feedbackVideoRef.current;
      if (!videoEl) return;
      videoEl.currentTime = 0;
      videoEl.volume = 0;
      const playPromise = videoEl.play();
      if (playPromise && typeof playPromise.then === 'function') {
        playPromise.catch(() => {});
      }
      const targetVolume = 0.5;
      const duration = 3000;
      const steps = 30;
      const stepDuration = duration / steps;
      let currentStep = 0;
      feedbackVolumeIntervalRef.current = setInterval(() => {
        currentStep += 1;
        const progress = Math.min(1, currentStep / steps);
        videoEl.volume = targetVolume * progress;
        if (progress >= 1) {
          clearFeedbackVolumeRamp();
        }
      }, stepDuration);
    } else {
      feedbackAutoAdvanceRef.current = setTimeout(() => {
        nextFeedback();
      }, 6000);
    }
    return () => {
      clearFeedbackAdvanceTimeout();
      clearFeedbackVolumeRamp();
    };
  }, [
    showFeedback,
    currentFeedbackIndex,
    currentFeedbackMedia.type,
    clearFeedbackAdvanceTimeout,
    clearFeedbackVolumeRamp,
    nextFeedback,
  ]);

  useEffect(
    () => () => {
      clearFeedbackAdvanceTimeout();
      if (feedbackVideoRef.current) {
        feedbackVideoRef.current.pause();
      }
      clearFeedbackVolumeRamp();
    },
    [clearFeedbackAdvanceTimeout, clearFeedbackVolumeRamp]
  );

  useEffect(() => {
    if (!showFeedback) {
      setIsFeedbackSwitching(false);
      if (feedbackSwitchTimeoutRef.current) {
        clearTimeout(feedbackSwitchTimeoutRef.current);
        feedbackSwitchTimeoutRef.current = null;
      }
      return;
    }

    setIsFeedbackSwitching(true);
    if (feedbackSwitchTimeoutRef.current) clearTimeout(feedbackSwitchTimeoutRef.current);
    feedbackSwitchTimeoutRef.current = setTimeout(() => {
      setIsFeedbackSwitching(false);
      feedbackSwitchTimeoutRef.current = null;
    }, 450);

    return () => {
      if (feedbackSwitchTimeoutRef.current) {
        clearTimeout(feedbackSwitchTimeoutRef.current);
        feedbackSwitchTimeoutRef.current = null;
      }
    };
  }, [currentFeedbackIndex, showFeedback]);

  /* ------------ paragraph fade-in for sections ------------ */
  useEffect(() => {
    document.querySelectorAll('.show-section p').forEach((p, i) => {
      p.classList.add('fade-in-paragraph');
      p.style.animationDelay = `${i * 0.1}s`;
    });
  }, [
    showHowItWorks,
    showGenres,
    showImportantToKnow,
    showFaq,
    showFeedback,
  ]);

  /* ------------ render ------------ */
  return (
    <div className="App submit-page">
      {/*──────────────── BACKGROUND GIFs ────────────────*/}
      <div id="background">
        <img
          src={require('./bg_noise/mobile_noise.gif')}
          alt=""
          aria-hidden="true"
          className="bg-gif mobile"
        />
        <img
          src={require('./bg_noise/tablet_noise.gif')}
          alt=""
          aria-hidden="true"
          className="bg-gif tablet"
        />
        <img
          src={require('./bg_noise/desktop_noise.gif')}
          alt=""
          aria-hidden="true"
          className="bg-gif desktop"
        />
        <img src={SUBMIT_BACKGROUND_GIF} alt="" aria-hidden="true" id="animated-gif" />
      </div>

      {/*──────────────── CONTENT ────────────────*/}
      <div id="content-wrapper">
        <div id="links" className={`submit-links ${isPurchaseFocusMode ? 'purchase-focus-active' : ''}`.trim()}>
          <div className="section-title hero-title" data-text="MUSIC SUBMISSION">
            MUSIC SUBMISSION
          </div>

      {/* ───────────── HERO SUBTITLE (track review summary) ───────────── */}
          <div className="hero-subtitle">
            <div className="hero-review-list" aria-label="Track review summary">
              <div className="hero-review-item">
                <div className="hero-review-item__title">Fast turnaround</div>
                <div className="hero-review-item__text">
                  <PlanWord plan="premium" intro revealed={revealedHeroPlans.includes('premium')}>Premium</PlanWord> up to 48h · <PlanWord plan="standard" intro revealed={revealedHeroPlans.includes('standard')}>Standard</PlanWord> and <PlanWord plan="quick" intro revealed={revealedHeroPlans.includes('quick')}>Quick Check</PlanWord> up to 5 days
                </div>
              </div>
              <div className="hero-review-item">
                <div className="hero-review-item__title">Written feedback</div>
                <div className="hero-review-item__text">
                  <PlanWord plan="quick" intro revealed={revealedHeroPlans.includes('quick')}>Quick Check</PlanWord> includes a brief written review · <PlanWord plan="standard" intro revealed={revealedHeroPlans.includes('standard')}>Standard</PlanWord> and <PlanWord plan="premium" intro revealed={revealedHeroPlans.includes('premium')}>Premium</PlanWord> include more detailed feedback
                </div>
              </div>
              <div className="hero-review-item">
                <div className="hero-review-item__title">Playlist consideration</div>
                <div className="hero-review-item__text">Approved tracks can move to best-fit Spotify playlist consideration</div>
              </div>
            </div>
            <div className="hero-review-trust" aria-label="Trust notes">
              <span className="hero-review-trust__lead">Clean approach only:</span>{' '}
              no bots, no fake streams, no guaranteed placement.
            </div>
          </div>

          <CuratorProofBadge
            dimmed={stampDimmed}
            bright={stampBright}
            fading={stampBrightFading}
            onActivate={flashCuratorStamp}
            onOpen={() => setShowCuratorTrust(true)}
          />

          {/* submission CTA */}
          <div id="get-your-music-heard" className="permanent-show-section">
            <div className={`purchase-flow purchase-step-${purchaseStep}`}>
              <div className="purchase-flow-viewport">
                <div className="purchase-flow-track">
                  <div
                    className="purchase-flow-panel purchase-flow-panel--plans"
                    aria-hidden={purchaseStep === 'confirm'}
                  >
                    <div className="purchase-flow-panel-inner">
                      <SubmissionButtons
                        selectedPlan={selectedPlan}
                        suppressSelection={!introComplete}
                        onClick={handleSubmissionClick}
                        flashPlan={flashPlan}
                        flashTick={flashTick}
                      />
                      <div className={`price-details ${showForm && introComplete ? '' : 'price-details--placeholder'}`.trim()}>
                        <p aria-hidden={!(showForm && introComplete)}>
                          Price: <span className="price-glow">{showForm && introComplete ? price : '$00'}</span>{' '}
                          {showForm && introComplete && discount ? <span className="discount-glow">({discount})</span> : null}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div
                    className="purchase-flow-panel purchase-flow-panel--confirm"
                    aria-hidden={purchaseStep !== 'confirm'}
                  >
                    <div className="purchase-flow-panel-inner">
                      {showForm && (
                        <CheckoutConfirmStep
                          agreed={agreed}
                          setAgreed={(value) => {
                            setAgreed(value);
                            if (value) {
                              trackSiteEvent('expectations_accepted', {
                                source_page: 'submit_your_music',
                                plan_key: selectedPlan,
                              });
                            }
                          }}
                          onOpenTerms={() => setShowTerms(true)}
                          onOpenRefundPolicy={() => setShowRefundPolicy(true)}
                          error={purchaseStep === 'confirm' ? submitError : ''}
                          selectedPlanLabel={selectedPlanLabel}
                          selectedPlanTone={selectedPlanTone}
                          price={price}
                        />
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <PurchaseFlowActions
                visible={showForm && introComplete}
                step={purchaseStep}
                canContinue={Boolean(selectedPlan) && !isSubmitting}
                canConfirm={agreed && !isSubmitting}
                isSubmitting={isSubmitting}
                onContinue={handleFormSubmit}
                onBack={() => {
                  if (isSubmitting) return;
                  setPurchaseStep('plans');
                  setSubmitError('');
                }}
                onConfirm={startPayment}
                selectedPlanTone={selectedPlanTone}
              />
            </div>
          </div>

          {/* nav buttons */}
          <button
            className="btn btn-link flicker"
            data-text="HOW IT WORKS"
            onClick={makeToggle(
              showHowItWorks,
              setShowHowItWorks,
              '.btn-link[data-text="HOW IT WORKS"]',
              'how-it-works'
            )}
          >
            HOW IT WORKS
          </button>
          {/* section content */}
          <div id="how-it-works" className={showHowItWorks ? 'show-section' : ''}>
            <p>• Choose a plan → pay securely → submit your track for independent curator review.</p>
            <p>• <PlanWord plan="quick">Quick Check</PlanWord>: brief written review.</p>
            <p>• <PlanWord plan="standard">Standard</PlanWord>: written feedback with more context.</p>
            <p>• <PlanWord plan="premium">Premium</PlanWord>: best if you want the most detailed feedback and the fastest review.</p>
            <p>• Released and Planned submissions also include an independent curator decision; Review only does not.</p>
            <p>• Review timing: <PlanWord plan="premium">Premium</PlanWord> up to 48 hours. <PlanWord plan="standard">Standard</PlanWord> and <PlanWord plan="quick">Quick Check</PlanWord> up to 5 days.</p>
            <p>• If it fits, it may be considered for the best-fit curator playlist.</p>
            <p>• Make sure your link/file is playable and access is granted — timelines start after I can open your track</p>
          </div>

          {/* other nav buttons & sections */}
          <button
            className="btn btn-link flicker"
            data-text="GENRES WE ACCEPT"
            onClick={makeToggle(
              showGenres,
              setShowGenres,
              '.btn-link[data-text="GENRES WE ACCEPT"]',
              'genres-we-accept'
            )}
          >
            GENRES WE ACCEPT
          </button>
          <div id="genres-we-accept" className={showGenres ? 'show-section' : ''}>
            <p>• alternative hip-hop • trap • drill • dark trap • trap metal • phonk • scream rap</p>
            <p>• cloud rap • glitchcore • breakcore • sigilkore • hyperpop</p>
            <p>• ambient • vaporwave • wave • dreampunk • lo-fi</p>
            <p>• shoegaze • post punk • metalcore • punk • post-hardcore</p>
            <p>• bass house • electro • neurofunk • dnb • halftime dnb • dark clubbing</p>
            <p>• reggaeton • afrobeats • dancehall • brazilian funk</p>
            <p>
              • *country • *americana • *indie folk • *acoustic • *singer-songwriter / *partner
              playlists
            </p>
          </div>

          <button
            className="btn btn-link flicker"
            data-text="IMPORTANT TO KNOW"
            onClick={makeToggle(
              showImportantToKnow,
              setShowImportantToKnow,
              '.btn-link[data-text="IMPORTANT TO KNOW"]',
              'important-to-know'
            )}
          >
            IMPORTANT TO KNOW
          </button>
          <div id="important-to-know" className={showImportantToKnow ? 'show-section' : ''}>
            <p>
              • Please make sure your track matches the playlist vibe,{' '}
              <a href="/playlists" className="playlist-context-link">explore the playlists</a> to feel each mood before you submit.
            </p>
            <p>• This service is provided in English. Feedback, review comments, and submission-related communication are delivered in English.</p>
            <p>• Curation is subjective — that’s the point of tastemaking.</p>
            <p>• The review plan changes the feedback format, level of detail, and review turnaround time. Playlist consideration is always based on the track itself, not on the plan you choose.</p>
            <p>• All playlists = real audience (no fake streams). Results depend on fit and followers.</p>
            <p>• I do not offer paid placement. Curation is based on quality, fit, vibe, and aesthetic, not on payment.</p>
            <p>• Guaranteed paid placement or guaranteed streams would also conflict with Spotify platform rules. This service is for review and playlist consideration only.</p>
          </div>

          <button
            className="btn btn-link flicker"
            data-text="FEEDBACK"
            onClick={makeToggle(
              showFeedback,
              setShowFeedback,
              '.btn-link[data-text="FEEDBACK"]',
              'feedback-gallery'
            )}
          >
            FEEDBACK
          </button>
          <div id="feedback-gallery" className={showFeedback ? 'show' : ''}>
            <div className="feedback-carousel">
              <button className="arrow arrow-left" onClick={prevFeedback}>
                &lt;
              </button>
              <div className={`feedback-media-frame ${isFeedbackSwitching ? 'glitch-swap' : ''}`}>
                {currentFeedbackMedia.type === 'video' ? (
                  <video
                    key={`feedback-video-${currentFeedbackIndex}`}
                    ref={feedbackVideoRef}
                    src={currentFeedbackMedia.src}
                    controls
                    playsInline
                    preload="metadata"
                    onEnded={nextFeedback}
                  />
                ) : (
                  <img
                    src={currentFeedbackMedia.src}
                    alt={`feedback ${currentFeedbackIndex + 1}`}
                  />
                )}
              </div>
              <button className="arrow arrow-right" onClick={nextFeedback}>
                &gt;
              </button>
            </div>
            {!hideInstagram && (
              <p className="feedback-highlight-note">
                <a
                  className={`red-link glitch-text ${highlightGlitch ? 'glitch-active' : ''}`}
                  data-text="More in my Feedback highlight"
                  href="https://www.instagram.com/ivagonzo/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  More in my Feedback highlight
                </a>
              </p>
            )}
          </div>

          <button
            className="btn btn-link flicker"
            data-text="FAQ"
            onClick={makeToggle(
              showFaq,
              setShowFaq,
              '.btn-link[data-text="FAQ"]',
              'submission-faq'
            )}
          >
            FAQ
          </button>
          <div id="submission-faq" className={showFaq ? 'show-section submit-seo-faq' : 'submit-seo-faq'}>
            <p>
              <strong>Can I submit music to Iva Gonzo Spotify playlists?</strong>
              <span>Yes — submit for independent curator review and possible playlist consideration when the track fits.</span>
            </p>
            <p>
              <strong>Is this paid playlist placement?</strong>
              <span>No — payment covers review, written feedback, and possible consideration. Placement, streams, saves, and followers are not guaranteed.</span>
            </p>
            <p>
              <strong>Does the review plan affect my chance of getting into a playlist?</strong>
              <span>No. The review plan does not increase the chance of playlist approval. It changes the feedback format, level of detail, and review turnaround time. Playlist decisions are based on the music, not on the plan you choose.</span>
            </p>
            <p>
              <strong>What genres are best for Iva Gonzo playlist submission?</strong>
              <span>Dark alternative and underground sounds usually fit best: dark trap, phonk, trap metal, scream rap, hyperpop, glitchcore, shoegaze, post-punk, metalcore, electronic, dnb, reggaeton, afrobeat, and related hybrids.</span>
            </p>
            <p>
              <strong>Can I submit unreleased music?</strong>
              <span>Yes — planned-release submissions can use a playable private link and/or uploaded audio file with the planned release date.</span>
            </p>
            <p>
              <strong>Can I submit an old release?</strong>
              <span>Yes. You can submit old releases. The release date does not affect the review. I focus on the track’s quality, genre fit, playlist vibe, and audience match.</span>
            </p>
            <p>
              <strong>Can I submit a track just for review if the release date is not planned yet?</strong>
              <span>Yes — Review only is available with Quick Check, Standard, and Premium. It includes written feedback only, and the track is not evaluated for playlist fit. To request playlist consideration later, use an available credit for a new Planned submission once the release date is confirmed, or a new Released submission once the track is live.</span>
            </p>
            <p>
              <strong>How long does review take?</strong>
              <span>Premium review is up to 48 hours. Standard and Quick Check are up to 5 days.</span>
            </p>
          </div>
        </div>

      </div>

      {/*───────────── SOCIAL ICONS ─────────────*/}
      <footer id="social-icons">
        <a href="/" aria-label="Back to home">
          <img
            className="social-icon"
            src={require('./soc icons/0_home.svg').default}
            alt="Home"
          />
        </a>
        {!hideInstagram && (
          <a
            href="https://www.instagram.com/ivagonzo/"
            target="_blank"
            rel="noopener noreferrer"
          >
            <img
              className="social-icon"
              src={require('./soc icons/1_icons8-instagram.svg').default}
              alt="Instagram"
            />
          </a>
        )}
        <a
          href="https://open.spotify.com/user/11185646448?si=e1a94395b1054a48"
          target="_blank"
          rel="noopener noreferrer"
        >
          <img
            className="social-icon"
            src={require('./soc icons/2_icons8-spotify.svg').default}
            alt="Spotify"
          />
        </a>
        <a href="https://youtube.com/@ivagonzo/" target="_blank" rel="noopener noreferrer">
          <img
            className="social-icon"
            src={require('./soc icons/3_icons8-youtube.svg').default}
            alt="YouTube"
          />
        </a>
        {/*<a href="https://www.tiktok.com/@ivagonzo/" target="_blank" rel="noopener noreferrer">
          <img
            className="social-icon"
            src={require('./soc icons/4_icons8-tiktok.svg').default}
            alt="TikTok"
          />
        </a>*/}
        <a href="https://www.pinterest.com/ivagonzo/" target="_blank" rel="noopener noreferrer">
          <img
            className="social-icon"
            src={require('./soc icons/5_icons8-pinterest.svg').default}
            alt="Pinterest"
          />
        </a>
        {/*<a href="https://x.com/ivagonzo/" target="_blank" rel="noopener noreferrer">
          <img
            className="social-icon"
            src={require('./soc icons/6_icons8-twitterx.svg').default}
            alt="TwitterX"
          />
        </a>*/}
        <a href="mailto:support@ivagonzo.com" aria-label="Email">
          <img
            className="social-icon"
            src={require('./soc icons/7_email.svg').default}
            alt="Email"
          />
        </a>
      </footer>

      {/*───────────── LEGAL LINKS & MODALS ─────────────*/}
      <div id="legal-links" className="legal-links">
        <button className="legal-link" onClick={() => setShowPrivacyPolicy(true)}>
          Privacy Policy
        </button>
        <button className="legal-link" onClick={() => setShowTerms(true)}>
          Terms & Conditions
        </button>
        <button className="legal-link" onClick={() => setShowRefundPolicy(true)}>
          Refund Policy
        </button>
      </div>

      <PaymentMaintenanceModal
        open={showPaymentMaintenance}
        onClose={() => setShowPaymentMaintenance(false)}
        source="submit_your_music"
      />

      {showPrivacyPolicy && (
        <Modal title="Privacy Policy" onClose={() => setShowPrivacyPolicy(false)}>
          <PrivacyPolicy />
        </Modal>
      )}
      {showTerms && (
        <Modal title="Terms & Conditions" onClose={() => setShowTerms(false)}>
          <Terms />
        </Modal>
      )}
      {showRefundPolicy && (
        <Modal title="Refund Policy" onClose={() => setShowRefundPolicy(false)}>
          <RefundPolicy />
        </Modal>
      )}
      {showCuratorTrust && (
        <Modal
          className="curator-trust-modal"
          ariaLabel="About Iva Gonzo curation trust"
          onClose={() => setShowCuratorTrust(false)}
        >
          <CuratorTrustContent
            hideInstagram={hideInstagram}
            onFeedbackLinkClick={openFeedbackFromTrust}
          />
        </Modal>
      )}
    </div>
  );
}

/*──────────────── SUB-COMPONENTS ────────────────*/
const CuratorProofBadgeStats = ({ className = 'curator-proof__content' }) => (
  <span className={className}>
    <span className="curator-proof__eyebrow">Curator mark</span>
    <span className="curator-proof__item">
      <strong>4,000+</strong>
      <span>tracks shared</span>
    </span>
    <span className="curator-proof__item">
      <strong>50k+</strong>
      <span>reviewed</span>
    </span>
    <span className="curator-proof__item">
      <strong>4 yrs</strong>
      <span>active</span>
    </span>
  </span>
);

const CuratorProofBadge = ({ dimmed, bright, fading, onActivate, onOpen }) => (
  <button
    type="button"
    className={`curator-proof ${dimmed ? 'is-dimmed' : ''} ${bright ? 'is-bright' : ''} ${fading ? 'is-bright-fading' : ''}`.trim()}
    aria-label="Curator track record"
    aria-haspopup="dialog"
    onClick={(event) => {
      event.preventDefault();
      onActivate();
      onOpen();
    }}
    onKeyDown={(event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        onActivate();
        onOpen();
      }
    }}
    onContextMenu={(event) => event.preventDefault()}
  >
    <span className="curator-proof__ring" aria-hidden="true" />
    <span className="curator-proof__veil" aria-hidden="true" />
    <CuratorProofBadgeStats />
    <span className="curator-proof__shine-lift" aria-hidden="true">
      <span className="curator-proof__shine-window">
        <span className="curator-proof__shine-copy">
          <span className="curator-proof__ring curator-proof__ring--shine" />
          <CuratorProofBadgeStats className="curator-proof__content curator-proof__content--shine" />
        </span>
      </span>
    </span>
  </button>
);

const CuratorTrustContent = ({ hideInstagram, onFeedbackLinkClick }) => (
  <article className="curator-trust-copy">
    <p>
      Iva Gonzo is an independent Dark Trap / Phonk artist, producer, digital art creator, and
      playlist curator. Her curation is rooted in the same alternative underground aesthetic she
      builds through her own music, visuals, playlists, and community.
    </p>
    {hideInstagram ? (
      <p>
        Her playlists are part of a wider creative ecosystem across Spotify, Apple Music, and
        YouTube Music, where she actively shares new artists, dark moods, visual identity, and real
        community feedback.
      </p>
    ) : (
      <p>
        Her playlists are part of a wider creative ecosystem across Spotify, Apple Music, YouTube
        Music, and{' '}
        <a href={INSTAGRAM_PROFILE_URL} target="_blank" rel="noopener noreferrer">
          Instagram
        </a>
        , where she actively shares new artists, dark moods, visual identity, and real community
        feedback.
      </p>
    )}
    <p>
      For more than 4 years, she has reviewed and curated music professionally across major music
      submission platforms including SubmitHub, Groover, and PlaylistPush, with her public{' '}
      <a href={SUBMITHUB_PROFILE_URL} target="_blank" rel="noopener noreferrer">
        SubmitHub curator profile
      </a>
      {' '}showing a 5/5 feedback quality rating from user reviews and multiple positive artist
      comments.
    </p>
    <p>
      Selected artist shoutout examples are featured in the{' '}
      <a href="#feedback-gallery" onClick={onFeedbackLinkClick}>
        feedback section
      </a>{' '}
      of this page.
    </p>
  </article>
);

const SubmissionButtons = ({ selectedPlan, suppressSelection = false, onClick, flashPlan, flashTick }) => {
  const renderTier = (tier, label, planPrefix, buttonExtraClass = '', titleClass = '') => (
    <div className={`submission-option ${tier}-column`}>
      <div className={`option-title ${titleClass}`.trim()}>{label}</div>
      <div className="option-buttons">
        {['1', '5', '10'].map((n) => {
          const plan = `${planPrefix}_${n}`;
          const isSelected = !suppressSelection && selectedPlan === plan;
          const isFlashing = flashPlan === plan;
          const flashClass = isFlashing ? (tier === 'premium' ? 'flash-gold' : 'flash-red') : '';
          const classes = [
            'submission-button',
            buttonExtraClass,
            isSelected ? 'selected' : '',
            flashClass,
          ]
            .filter(Boolean)
            .join(' ');

          return (
            <button
              key={`${n}-${tier}-${isFlashing ? flashTick : 'k'}`}
              data-plan={plan}
              className={classes}
              onClick={() => onClick(plan)}
            >
              <span className="btn-glass" aria-hidden="true"></span>
              <span className="btn-label">
                <span className="big">{n} {tier === 'premium' ? 'Premium' : 'Standard'}</span>
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );

  return (
    <div className="submission-buttons">
      <div className="submission-option quick-check-wrap">
        <button
          key={`quick-${flashPlan === 'quick_check' ? flashTick : 'k'}`}
          data-plan="quick_check"
          className={`submission-button quick ${!suppressSelection && selectedPlan === 'quick_check' ? 'selected' : ''} ${
            flashPlan === 'quick_check' ? 'flash-cyan' : ''
          }`}
          onClick={() => onClick('quick_check')}
        >
          <span className="btn-glass" aria-hidden="true"></span>
          <span className="btn-label">
            <span className="big">Quick Check</span>
          </span>
        </button>
      </div>

      <div className="submission-options-duo">
        {renderTier('standard', 'Standard Reviews', 'standard')}
        {renderTier('premium', 'Premium Reviews', 'premium', 'premium', 'premium-title')}
      </div>
    </div>
  );
};

const CheckoutConfirmStep = ({
  agreed,
  setAgreed,
  onOpenTerms,
  onOpenRefundPolicy,
  error,
  selectedPlanLabel,
  selectedPlanTone,
  price,
}) => (
  <div className="checkout-confirm-inline">
    <div className="checkout-confirm-summary">
      <span className="checkout-confirm-summary__label">Selected</span>
      <span className={`checkout-confirm-summary__plan checkout-confirm-summary__plan--${selectedPlanTone}`}>
        {selectedPlanLabel}
      </span>
      {price ? <span className="checkout-confirm-summary__price">· {price}</span> : null}
    </div>
    <h3>Before you continue</h3>
    <div className="checkout-confirm-copy">
      <p>You are purchasing {selectedPlanLabel} — a prepaid music review service.</p>
      <p>Playlist placement is possible, but not guaranteed. Guaranteed streams are also not offered.</p>
    </div>
    <label className="checkout-confirm-checkbox">
      <input
        type="checkbox"
        checked={agreed}
        onChange={(event) => setAgreed(event.target.checked)}
      />
      <span>
        I understand that playlist placement and streaming results are not guaranteed, and I agree to the{' '}
        <button
          type="button"
          className="checkout-confirm-terms-link"
          onClick={(event) => {
            event.preventDefault();
            onOpenTerms();
          }}
        >
          Terms &amp; Conditions
        </button>
        {' '}and{' '}
        <button
          type="button"
          className="checkout-confirm-terms-link"
          onClick={(event) => {
            event.preventDefault();
            onOpenRefundPolicy();
          }}
        >
          Refund Policy
        </button>
        .
      </span>
    </label>
    <div className="checkout-confirm-feedback-zone">
      {!agreed && !error && (
        <div className="checkout-confirm-helper">Check the box to continue to payment.</div>
      )}
      {error && <div className="checkout-confirm-error">{error}</div>}
    </div>
  </div>
);

const PurchaseFlowActions = ({
  visible,
  step,
  canContinue,
  canConfirm,
  isSubmitting,
  onContinue,
  onBack,
  onConfirm,
  selectedPlanTone,
}) => {
  const isConfirmStep = step === 'confirm';
  const primaryEnabled = isConfirmStep ? canConfirm : canContinue;
  const primaryLabel = isSubmitting
    ? 'Redirecting...'
    : isConfirmStep
      ? 'Continue to payment'
      : 'Continue';
  const primaryAction = isConfirmStep ? onConfirm : onContinue;

  return (
    <div className={`purchase-flow-actions purchase-flow-actions--${step} ${visible ? '' : 'purchase-flow-actions--hidden'}`.trim()}>
      <button
        type="button"
        className={`purchase-flow-primary purchase-flow-primary--${selectedPlanTone} ${primaryEnabled ? 'enabled' : ''} ${
          primaryEnabled && !isSubmitting ? 'attention-pulse' : ''
        }`.trim()}
        onClick={primaryAction}
        disabled={!primaryEnabled}
      >
        <span className="btn-glass" aria-hidden="true"></span>
        <span className="btn-label">{primaryLabel}</span>
      </button>
      <div className="purchase-flow-actions__secondary-slot">
        {isConfirmStep ? (
          <button
            type="button"
            className="checkout-confirm-secondary"
            onClick={onBack}
            disabled={isSubmitting}
          >
            Go back
          </button>
        ) : (
          <span className="checkout-confirm-secondary checkout-confirm-secondary--placeholder" aria-hidden="true">
            Go back
          </span>
        )}
      </div>
    </div>
  );
};

const Modal = ({ title, children, onClose, className = '', ariaLabel = '' }) => (
  <div
    className="modal-overlay"
    onPointerDown={(event) => {
      if (event.target === event.currentTarget) {
        onClose();
      }
    }}
  >
    <div
      className={`modal ${className}`.trim()}
      role="dialog"
      aria-modal="true"
      aria-label={ariaLabel || title || 'Dialog'}
      onClick={(e) => e.stopPropagation()}
    >
      <button className="close-button" onClick={onClose}>
        ×
      </button>
      {title ? <h2>{title}</h2> : null}
      {children}
    </div>
  </div>
);

// =====================
// PRIVACY POLICY
// =====================

const PrivacyPolicy = () => (
  <article className="legal-text">
    <p><strong>Last updated:</strong> 1 August 2026</p>
    <p>
      <strong>Who we are.</strong> This service is operated by <strong>Iva Gonzo</strong> (independent music curator).
      <strong> Data controller (where applicable):</strong> Iva Gonzo. For privacy questions or requests, contact{' '}
      <a href="mailto:support@ivagonzo.com">support@ivagonzo.com</a>.
    </p>
    <p>
      <strong>What we collect.</strong> Your email, artist/stage name, track links, optional notes, and any audio files you upload for review.
      If you request a payment-availability notification, we also collect the email address you submit for that purpose and the type of request indicated by the page on which you requested it (track submissions or support).
      We may also process basic technical data and limited purchase references received from the payment provider (such as order/payment IDs, purchase date/time,
      payment status, amount, currency, device/browser info, and IP address) for security, fraud prevention, troubleshooting, and record-keeping.
      We do not store your card or banking details.
    </p>
    <p>
      <strong>Legal basis (where applicable).</strong> We process personal data to perform our contract with you (deliver the service you purchased),
      to comply with legal obligations, and for legitimate interests such as security and fraud prevention. If you request a payment-availability notification,
      we use your email to provide that requested update.
    </p>
    <p>
      <strong>How we use it.</strong> Solely to deliver the feedback service you purchased (review your track, communicate with you, manage submissions,
      process refunds and manage prepaid reviews, prevent fraud/abuse, and keep required business records), or, if you request it, to confirm your signup and send a payment-availability update.
      We do not sell your personal data.
    </p>
    <p>
      <strong>Payments.</strong> Payments are processed by the payment provider shown at checkout. Card and billing data are processed by that provider
      and its payment partners under their own terms and privacy policies. We receive only limited transaction information and purchase references
      (for example, order/payment IDs, payment status, amount, and currency) needed to deliver the service, manage prepaid reviews, send receipts, prevent fraud,
      and resolve disputes. We do not store or have access to your full card or banking details.
    </p>
    <p>
      <strong>Service providers.</strong> To run the website and deliver the service, we use trusted providers (for example,
      third-party checkout and payment providers, hosting/storage, and email/support tools). We share only the minimum
      data needed to provide their services to us. We require (or contractually expect) them to use it only to provide services
      to us and to protect it appropriately. We do not sell your data. We may also disclose information if required by law.
    </p>
    <p>
      <strong>Advertising and measurement.</strong> Public pages may use <strong>Meta Pixel</strong> to measure advertising performance and build
      retargeting audiences based on non-sensitive interactions with public pages. We also use first-party analytics to understand
      page visits, referral sources, and the submission funnel. Meta may process technical data such as IP address,
      browser/device information, event timestamps, and event context under its own
      terms. We do not send uploaded audio files, private track links, submission notes, or full payment card/banking details to Meta through this pixel.
    </p>
    <p>
      <strong>Audio & private links.</strong> Uploaded audio files and private links (SoundCloud private URLs, unlisted YouTube links, etc.) are treated as confidential
      and are never published or shared. You are responsible for maintaining access to any private links you provide during the service (permissions/passwords/availability).
    </p>
    <p>
      <strong>Retention.</strong> Uploaded audio files are automatically deleted after 30 days of submission (test mode may shorten this to 10 minutes).
      Other data (e.g., emails, order records, support messages) may be retained as long as needed to provide the service, resolve disputes/chargebacks,
      prevent fraud, and comply with legal obligations. Payment-availability notification requests are retained only until that update is sent or you ask us to delete them.
    </p>
    <p>
      <strong>Your rights.</strong> You can request access, correction or deletion of your data by emailing <a href="mailto:support@ivagonzo.com">support@ivagonzo.com</a>.
      If you want us to delete your uploaded audio earlier, just ask. Where applicable, you may also have the right to object to processing and to lodge a complaint
      with your local data protection authority.
    </p>
    <p>
      <strong>Security.</strong> We use reasonable safeguards to protect your data and access to submissions. However, no online service can be guaranteed to be completely secure.
    </p>
    <p>
      <strong>Transfers & storage.</strong> Data may be stored/processed on servers located in the EU/US or other countries where our providers operate.
      Appropriate technical and organizational safeguards are applied.
    </p>
    <p>
      <strong>Cookies.</strong> We use essential cookies and similar technologies required for basic website functionality and security. Public
      pages may also use Meta Pixel and first-party analytics for advertising measurement, retargeting, and site performance analysis as described above.
    </p>
    <p><strong>Changes.</strong> Updates will be posted here. Continued use after changes means you accept the updated policy.</p>
  </article>
);


// =====================
// TERMS & CONDITIONS
// =====================

const Terms = () => (
  <article className="legal-text">
    <p>
      <strong>1. Service.</strong> You are purchasing a digital music <strong>review and consideration</strong> service. Approval and playlist placement are <strong>not guaranteed</strong>.
      If approved, sharing/placement may be done on one or more curator playlists on platforms such as <strong>Spotify, Apple Music, and YouTube Music</strong> (where technically possible).
      Guaranteed paid playlist placement or guaranteed stream promises would conflict with platform rules, including Spotify platform rules, so this product is sold as review and consideration only.
      We are an independent curator and are not affiliated with Spotify, Apple, YouTube, or any other third-party platform. Unless explicitly agreed otherwise, this service is provided in English.
      Feedback, review comments, submission-related updates, and support communication are delivered in English.
    </p>
    <p>
      <strong>2. Rights & permissions.</strong> By submitting any audio file or providing a link, you confirm you own the rights or have explicit permission from all rightsholders
      to share it with us for listening and review under these terms. Uploaded files may be temporarily stored for up to 30 days (or shorter in test mode).
      Links are not re-hosted; we may store the URL only as needed to perform the service and keep required business records.
    </p>
    <p>
      <strong>3. Payment.</strong> Prices are in USD. Payments are processed by the payment provider shown at checkout.
      We do not store or have access to your full card or banking details. You receive an order confirmation and submission link immediately after purchase.
    </p>
    <p>
      <strong>3a. Prepaid Reviews.</strong> Each prepaid review covers one track submission for the selected Iva Gonzo review service.
      <strong> Paid reviews remain available until used.</strong> <strong>Complimentary, bonus, courtesy, or reactivated reviews are not money</strong> and cannot be redeemed for cash or a cash refund.
    </p>    
    <p>
      <strong>4. Eligibility.</strong> You must be at least 18 years old (or the age of legal majority in your country) to purchase and use this service.
    </p>
    <p>
      <strong>5. Submission materials &amp; access.</strong> You must provide either (a) one playable audio file, or (b) one playable third-party link. If you provide a link, you are
      responsible for ensuring we have access (permissions/password if needed) and that it remains available until the service is completed. If the file is corrupted/unsupported,
      or the link is incorrect, unavailable, geo-restricted, or access is not granted, we will request an updated file/link. Delivery timeframes start (and resume) only after we
      receive a <strong>valid, accessible</strong> track. If you do not provide a valid submission within <strong>14 days</strong> of our request, we may close the submission as incomplete after notifying you;
      in such case, you may need to purchase/submit again, and refunds/overdue remedies do not apply. <strong>We start working</strong> when we first access the submitted file/link and begin listening/reviewing.
    </p>
    <p>
      <strong>6. Review delivery time.</strong> Review timeframes are counted from the moment we receive a valid submission (a playable file or accessible link).
      <PlanWord plan="quick">Quick Check</PlanWord>: up to 5 days. <PlanWord plan="standard">Standard</PlanWord>: up to 5 days. <PlanWord plan="premium">Premium</PlanWord>: up to 48 hours. If we miss a promised review timeframe,
      your remedies are described in the Refund Policy.
    </p>
    <p>
      <strong>7. Outcome.</strong> “Approved” = your track enters our placement queue and may be shared on one or more playlists (where technically possible). “Reviewed” = you still receive feedback, but no placement.
      Review only includes written feedback but no playlist decision. Any later playlist consideration requires a separate Planned or Released submission and an available review credit.
      For Released and Planned submissions, <PlanWord plan="quick">Quick Check</PlanWord> includes a brief written review and an independent curator decision. <PlanWord plan="standard">Standard</PlanWord>/<PlanWord plan="premium">Premium</PlanWord> include more detailed written feedback. The curator’s decision is final and subjective. We do not guarantee any results
      such as streams, saves, followers, algorithmic boosts, charting, press coverage, or career outcomes. Platform rules, including Spotify platform rules, are one reason we do not sell guaranteed placement or guaranteed stream outcomes.
    </p>
    <p>
      <strong>8. Placement.</strong> Placement applies to <strong>approved submissions only</strong> and, where it occurs, may be made on one or more curator playlists where technically possible.
      Placement is not guaranteed and is not a separately purchased service outcome.
    </p>
    <p>
      <strong>9. Track availability (platform/artist removal).</strong> If a track becomes unavailable on a platform (for example, removed by the artist/label/distributor, made private, geo-restricted, taken down, or otherwise inaccessible),
      this is outside our control. In such cases, we are not responsible for interruptions, and it does not create a right to refunds or replacement reviews.
    </p>
    <p>
      <strong>10. Intellectual property.</strong> All rights to your music remain yours. Written feedback is © Iva Gonzo; you may quote it for personal/promo use.
      You grant us a limited license to access, stream, and temporarily store your submission solely to deliver the service.
    </p>
    <p>
      <strong>11. Liability.</strong> Our maximum liability is limited to the amount you paid for the affected submission. To the maximum extent permitted by law, we are not liable for indirect, incidental, special,
      consequential, or punitive damages (including lost profits/opportunities), even if advised of the possibility.
    </p>
    <p>
      <strong>12. Chargebacks.</strong> Please contact us first — we resolve issues quickly. If needed, use the dispute process offered by your payment provider or card issuer within its applicable deadlines.
      Unjustified disputes or chargebacks may be formally contested using order logs, payment records, submission timestamps, portal activity,
      delivery evidence, support communications, and relevant platform records. Proven abuse or fraudulent disputes may lead to restrictions or a ban on future purchases.
    </p>
    <p>
      <strong>13. Changes.</strong> New terms apply to new purchases only.
    </p>
    <p>
      <strong>14. Re-submissions.</strong> Tracks that were previously approved/placed (status “Shared”) cannot be re-submitted as <em><PlanWord plan="quick">Quick Check</PlanWord></em>. Use <PlanWord plan="standard">Standard</PlanWord> or <PlanWord plan="premium">Premium</PlanWord> instead.
    </p>
    <p>
      <strong>15. Prohibited content & abuse.</strong> You must not submit illegal content, content you don’t have rights to share, malware, deceptive metadata, spam, or harassment.
      We may refuse, suspend, or stop providing the service (and/or restrict future purchases) in cases of abuse, fraud, harassment, or suspected rights violations.
    </p>
    <p>
      <strong>16. Right to refuse service.</strong> We may, <strong>as permitted by law</strong>, refuse, suspend, or cancel any order/submission at our discretion (for example,
      suspected fraud/abuse/harassment, rights or platform compliance concerns, technical impossibility, or safety reasons). We may not be able
      to provide detailed reasons. If we cancel <strong>before</strong> we start working on your submission (before listening/review), you will receive a
      <strong> full refund</strong> (or, for a multi-review purchase, the unused prepaid review will be restored). If we cancel due to your breach of these terms
      (e.g., rights violations, abusive behavior, or fraudulent activity), refunds or replacement reviews may be denied to the maximum extent permitted by law.
    </p>
    <p>
      <strong>17. Third-party services.</strong> We are not responsible for outages, takedowns, restrictions, policy enforcement, or changes by third-party platforms (e.g., Spotify/YouTube/SoundCloud).
      This includes platform rules that may restrict or prohibit guaranteed paid placement, stream manipulation, or similar conduct.
      Links to third-party services are provided/used for convenience and remain governed by their own terms.
    </p>
    <p>
      <strong>18. Disclaimer.</strong> The service is provided on an “as is” and “as available” basis to the maximum extent permitted by law. We do not make warranties that the service will be uninterrupted,
      error-free, or meet every expectation.
    </p>
    <p>
      <strong>19. Indemnity.</strong> You agree to indemnify and hold us harmless from claims, damages, losses, and expenses (including reasonable legal fees) arising out of your submission, your breach of these terms,
      or any allegation that you lacked rights/permissions for the content you provided.
    </p>
    <p>
      <strong>20. Force majeure &amp; maintenance.</strong> We are not responsible for delays or failure caused by events beyond our reasonable control (e.g., outages, infrastructure failures, platform downtime, force majeure).
      We may temporarily suspend access for maintenance/security.
    </p>
    <p>
      <strong>21. Disputes.</strong> If you have an issue, contact us first at{' '}
      <a href="mailto:support@ivagonzo.com">support@ivagonzo.com</a> and we’ll try to resolve it informally.
      If needed, you may also use the official refund or dispute process offered by your payment provider or card issuer, within its applicable deadlines.
      Contacting support first does not replace or extend those deadlines.
      Nothing in these terms limits your mandatory consumer rights where applicable.
    </p>
    <p>
      <strong>22. Severability.</strong> If any part of these terms is found invalid or unenforceable, the remaining parts stay in effect.
    </p>
    <p>
      <strong>23. Electronic communications.</strong> By using the service, you agree that notices and communications may be provided electronically (e.g., by email).
    </p>
    <p><strong>Contact:</strong> <a href="mailto:support@ivagonzo.com">support@ivagonzo.com</a></p>
  </article>
);


// =====================
// REFUND POLICY
// =====================

const RefundPolicy = () => (
  <article className="legal-text">
    <p>
      <strong>1. Return period.</strong> You may request a refund within the return/withdrawal period shown on the checkout page and/or in your receipt email
      (this period is at least <strong>14 days</strong> where applicable and may be longer depending on applicable law and country). All refunds are processed via the original payment method.
    </p>
    <p>
      <strong>2. Before you submit your track.</strong> If you have <strong>not submitted your track (file or link) yet</strong>, you can cancel your order within the return/withdrawal period described in section 1
      and receive a <strong>full refund</strong>.
    </p>
    <p>
      <strong>3. Start of service during the withdrawal period (where applicable).</strong> Where consumer withdrawal/cancellation rights apply, by submitting your track you request that we begin performing the service immediately.
      You acknowledge that, to the extent permitted by law, once the <strong>review outcome</strong> has been delivered, the service is considered performed and your right of withdrawal may no longer apply.
    </p>
    <div>
      <p>
        <strong>4. After you submit your track &amp; review outcome is delivered.</strong> Once we have listened to your track and delivered the <strong>review outcome</strong>, refunds are not granted,
        because the review/consideration service has been provided. For Released and Planned <strong><PlanWord plan="quick">Quick Check</PlanWord></strong> submissions, the “review outcome” includes a brief written review and an independent curator decision. A Review only outcome does not include a playlist decision. For <strong><PlanWord plan="standard">Standard</PlanWord>/<PlanWord plan="premium">Premium</PlanWord></strong>,
        the outcome includes more detailed written feedback. Approval and playlist placement are not guaranteed.
      </p>
      <blockquote>
        <p><strong>Important:</strong> Approval and playlist placement are not guaranteed.</p>
        <p>
          You are paying for a professional review and consideration, not for guaranteed playlist placement. A refund is not possible just because your track was not added to a playlist.
          Guaranteed paid placement or guaranteed stream promises would conflict with platform rules, including Spotify platform rules.
        </p>
      </blockquote>
    </div>
    <div>
      <p><strong>5. Multi-review purchases.</strong></p>
      <ul>
        <li>A full refund within the return/withdrawal period described in section 1 is possible only if no prepaid reviews from the purchase were used.</li>
        <li>As soon as any prepaid review from a multi-review purchase is used, cash refunds for the whole purchase are no longer available, <strong>except where required by law</strong>.</li>
        <li><strong>Complimentary/bonus/courtesy/reactivated reviews.</strong> Any complimentary, bonus, courtesy, or reactivated reviews are not money and are not eligible for cash refunds or redemption.</li>
        <li>
          If a single submission from a multi-review purchase is refunded <strong>where required by law</strong> or at <strong>our sole discretion</strong>, the refundable amount is calculated <strong>pro-rata</strong>:
        </li>
      </ul>
      <blockquote>
        <p>refund per track = total price of the multi-review purchase ÷ number of prepaid reviews in that purchase.</p>
        <p>The remaining unused paid reviews remain available for future submissions.</p>
      </blockquote>
    </div>
    <div>
      <p>
        <strong>6. Missed review deadline.</strong> If we fail to deliver your review within the promised review timeframe <strong>despite having a valid submission</strong>,
        the terms below apply:
      </p>

      <ul>
        <li>
          <strong>Review deadlines (counted from a valid submission):</strong> <PlanWord plan="quick">Quick Check</PlanWord> &amp; <PlanWord plan="standard">Standard</PlanWord>: up to 5 days. <PlanWord plan="premium">Premium</PlanWord>: up to 48 hours.
        </li>
      </ul>

      <p>
        <strong>Pauses/exclusions.</strong> Deadlines are paused (and overdue remedies do not apply) while we do not have a valid submission, including cases of inaccessible/incorrect links,
        missing permissions/passwords, corrupted/unsupported files, geo-restrictions, or when the track is removed/made private/taken down before review is complete.
        We are also not responsible for delays caused by third-party platform outages or restrictions beyond our control.
      </p>
      <p>
        <strong>Abandoned/incomplete submissions.</strong> If we request an updated file/link and you do not provide a valid, accessible submission within <strong>14 days</strong>,
        we may close the submission as incomplete. In that case, missed-deadline remedies do not apply.
      </p>
      <p>
        If we miss a promised review timeframe <strong>despite having a valid submission</strong>, our standard remedy is to
        <strong> re-activate the overdue submission</strong> and add <strong>one additional review of the same type</strong> to your account as compensation (if applicable).
      </p>
      <p>
        This <strong>restore + complimentary review</strong> remedy is the standard resolution for missed review deadlines. Monetary refunds for missed review deadlines are
        <strong> not offered as a standard client-selected option</strong>.
      </p>
      <p>
        A monetary refund for a missed review deadline may be granted only <strong>where required by law</strong> or at <strong>our sole discretion</strong>.
        For multi-review purchases, any such refund for an affected submission is calculated <strong>pro-rata</strong> under section 5, while the remaining unused paid reviews stay active.
      </p>

    </div>
    <p>
      <strong>7. Technical issues.</strong> If there is a technical problem on our side (file upload error, server issue, etc.), we will help you re-submit your track,
      restore the affected submission, or otherwise correct the issue. A monetary refund may be granted only <strong>where required by law</strong> or at <strong>our sole discretion</strong>.
    </p>
    <div>
      <p>
        <strong>8. How to request a refund.</strong> Please contact{' '}
        <a href="mailto:support@ivagonzo.com">support@ivagonzo.com</a> first with your <strong>Order ID</strong> (shown on the confirmation page and in your receipt email) if you need help.
        If needed, you may also use the official refund or dispute process offered by your payment provider or card issuer, within its applicable deadlines.
        Contacting support first does not replace or extend those deadlines. Any request for a monetary refund is reviewed under this policy and, when approved, is processed via the original payment method.
      </p>
      <p>
        <strong>9. Refund execution.</strong> Refunds are issued using the original payment method. After a refund is initiated,
        the time for the funds to appear in your account depends on your bank/payment provider.
        If you believe your payment method was used without authorization, contact your bank immediately.
      </p>
      <p>
        <strong>10. Orders cancelled by us.</strong> If we cancel an order/submission <strong>before</strong> we start working on it (before listening/review),
        you will receive a <strong>full refund</strong> (or, for a multi-review purchase, the unused prepaid review will be restored). If the cancellation is due to
        your breach of the Terms (e.g., rights violations, abusive behavior, fraud), refunds or replacement reviews may be denied to the maximum extent permitted by law.
      </p>
      <p>
        If your order includes a client portal or an “Overdue” feature, the portal may offer re-activation and complimentary-review remedies. It does not create an automatic right to a monetary refund.
      </p>
    </div>
    <p>
      <strong>11. Chargebacks.</strong> Please contact us first. If needed, use the dispute process offered by your payment provider or card issuer within its applicable deadlines.
      Unjustified disputes or chargebacks may be formally contested using order logs, payment records, submission timestamps, portal activity,
      delivery evidence, support communications, and relevant platform records. Proven abuse may lead to restrictions for future purchases.
    </p>
    <p>
      <strong>12. Statutory rights &amp; payment processor discretion.</strong> Nothing in this policy limits your mandatory consumer rights where applicable.
      In exceptional cases (e.g., suspected fraud or abuse), the payment processor may process a refund at its discretion.
    </p>
  </article>
);

export default SubmitYourMusicPage;
